import React, { useState, useEffect } from 'react';
import { ActivityType } from '../models/diary';
import { CreateActivityTypeData } from '../services/activityService';
import { useIsMobile } from '../hooks/useIsMobile';

interface ActivityTypeFormModalProps {
  open: boolean;
  item?: ActivityType;
  onSave: (data: CreateActivityTypeData) => Promise<void>;
  onCancel: () => void;
}

const ActivityTypeFormModal: React.FC<ActivityTypeFormModalProps> = ({ open, item, onSave, onCancel }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<{ name?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const isMobile = useIsMobile();
  const isEdit = !!item;

  useEffect(() => {
    if (open) {
      setName(item?.name || '');
      setDescription(item?.description || '');
      setErrors({});
      setSubmitError('');
    }
  }, [open, item]);

  const handleSave = async () => {
    if (!name.trim()) {
      setErrors({ name: 'El nombre es requerido' });
      return;
    }
    setIsSubmitting(true);
    setSubmitError('');
    try {
      await onSave({ name: name.trim(), description: description.trim() || null });
    } catch (error: any) {
      const resp = error.response;
      if (resp && Array.isArray(resp.name) && resp.name.length > 0) {
        setErrors({ name: resp.name[0] });
        setSubmitError('Por favor corrige los errores en el formulario.');
      } else {
        setSubmitError(resp?.error || error.message || 'Error al guardar. Por favor intente nuevamente.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
      background: 'rgba(0,0,0,0.18)', display: 'flex', alignItems: 'center',
      justifyContent: 'center', zIndex: 1100, padding: isMobile ? '12px' : '20px',
    }}>
      <div style={{
        background: '#fff', borderRadius: 12, padding: isMobile ? '24px 20px' : '32px 40px',
        width: '100%', maxWidth: 480, maxHeight: '90vh', overflow: 'auto',
        boxShadow: '0 4px 32px rgba(0,0,0,0.13)',
      }}>
        <h2 style={{ marginTop: 0, marginBottom: 20, fontSize: 22, fontWeight: 600, color: '#1f2937' }}>
          {isEdit ? 'Editar Tipo de Actividad' : 'Nuevo Tipo de Actividad'}
        </h2>

        {submitError && (
          <div style={{ background: '#fee2e2', color: '#dc2626', padding: '12px 16px', borderRadius: 8, marginBottom: 20, fontSize: 14 }}>
            {submitError}
          </div>
        )}

        <label style={{ display: 'block', marginBottom: 18 }}>
          <span style={{ fontWeight: 600, marginBottom: 8, display: 'block', fontSize: 14, color: '#374151' }}>
            Nombre <span style={{ color: '#dc2626' }}>*</span>
          </span>
          <input
            value={name}
            onChange={e => { setName(e.target.value); if (errors.name) setErrors({}); setSubmitError(''); }}
            style={{
              width: '100%', padding: '11px 14px',
              border: `1px solid ${errors.name ? '#dc2626' : '#cbd5e1'}`,
              borderRadius: 6, fontSize: 15, outline: 'none', boxSizing: 'border-box',
            }}
            placeholder="Ej: running"
          />
          {errors.name && <span style={{ color: '#dc2626', fontSize: 13, marginTop: 6, display: 'block' }}>{errors.name}</span>}
        </label>

        <label style={{ display: 'block', marginBottom: 28 }}>
          <span style={{ fontWeight: 600, marginBottom: 8, display: 'block', fontSize: 14, color: '#374151' }}>
            Descripción
          </span>
          <textarea
            value={description}
            onChange={e => { setDescription(e.target.value); setSubmitError(''); }}
            rows={3}
            style={{
              width: '100%', padding: '11px 14px', border: '1px solid #cbd5e1',
              borderRadius: 6, fontSize: 15, outline: 'none', boxSizing: 'border-box', resize: 'vertical',
            }}
            placeholder="Ej: Correr al aire libre"
          />
        </label>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', paddingTop: 8 }}>
          <button
            onClick={onCancel}
            disabled={isSubmitting}
            style={{
              background: '#f3f4f6', border: 'none', borderRadius: 6, padding: '11px 24px',
              fontWeight: 600, fontSize: 15, cursor: isSubmitting ? 'not-allowed' : 'pointer',
              opacity: isSubmitting ? 0.6 : 1, color: '#374151',
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={isSubmitting}
            style={{
              background: isSubmitting ? '#81c9c4' : '#38b2ac', color: '#fff', border: 'none',
              borderRadius: 6, padding: '11px 24px', fontWeight: 600, fontSize: 15,
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
            }}
          >
            {isSubmitting ? 'Guardando...' : isEdit ? 'Guardar Cambios' : 'Crear Tipo'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActivityTypeFormModal;
